import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

type SecondaryHeaderContextValue = {
  content: ReactNode;
  setContent: (content: ReactNode) => void;
};

const SecondaryHeaderContext = createContext<SecondaryHeaderContextValue | null>(
  null,
);

/**
 * Wraps the router (see main.tsx) so `<RootLayout>` — which renders the
 * shell's `secondaryHeader` slot — and individual leaf page routes
 * (rendered BELOW it, inside its own `<Outlet>`) can share one piece of
 * state despite not being parent/child of each other. Pages call
 * `useSecondaryHeader(node)` to publish into the slot; `<RootLayout>` calls
 * `useSecondaryHeaderContent()` to read whatever's currently published.
 */
export function SecondaryHeaderProvider({ children }: { children: ReactNode }) {
  const [content, setContent] = useState<ReactNode>(null);
  const value = useMemo(() => ({ content, setContent }), [content]);
  return (
    <SecondaryHeaderContext.Provider value={value}>
      {children}
    </SecondaryHeaderContext.Provider>
  );
}

function useSecondaryHeaderContext(): SecondaryHeaderContextValue {
  const ctx = useContext(SecondaryHeaderContext);
  if (!ctx) {
    throw new Error(
      "useSecondaryHeader(Content) must be used within a SecondaryHeaderProvider",
    );
  }
  return ctx;
}

/** Read by `<RootLayout>` to render whatever the active page has published. */
export function useSecondaryHeaderContent(): ReactNode {
  return useSecondaryHeaderContext().content;
}

/**
 * Called by a leaf page route to publish its filters/actions into the
 * shell's secondary header row. Clears automatically on unmount (route
 * change), so a page that never calls this renders no secondary header at
 * all, and one page's content never bleeds into the next.
 *
 * IMPORTANT: `content` must be referentially stable (wrap it in `useMemo`
 * keyed on its real dependencies before passing it in) — this effect's
 * dependency array is `[content, setContent]`, so a fresh JSX literal
 * created on every render of the calling component would re-fire the
 * effect every render, which re-renders the provider (and therefore the
 * calling page) every time: an infinite render loop, not just a one-off
 * mistake.
 */
export function useSecondaryHeader(content: ReactNode): void {
  const { setContent } = useSecondaryHeaderContext();
  useEffect(() => {
    setContent(content);
    return () => setContent(null);
  }, [content, setContent]);
}
